import {Link} from "react-router-dom";
import {AlertTriangle, ExternalLink, Send} from "lucide-react";
import {
  canPushOrderToSymphonya,
  symphonyaErrorHint,
  symphonyaOrderStatus,
  symphonyaResendButtonLabel,
  type AdminOrderDetail
} from "@corporatesclub/shared";
import {Button} from "@/components/ui/button";
import {AdminAlert, AdminBadge} from "../ui";
import {SymphonyaOrderBadge} from "./SymphonyaOrderBadge";

type Props = {
  detail: AdminOrderDetail;
  sending: boolean;
  onResend: () => void;
};

export function OrderSymphonyaPanel({detail, sending, onResend}: Props) {
  const status = symphonyaOrderStatus(detail.symphonya);
  const canPush = canPushOrderToSymphonya(detail.delivery.locatie);
  const cancelled = detail.status === 4;
  const showError = status.tone === "error" || detail.symphonyaError.trim() !== "";
  const errorText = detail.symphonyaError.trim() || (status.tone === "error" ? "Symphonya push failed (no error message stored)." : "");
  const errorHint = symphonyaErrorHint(errorText);

  return (
    <div className="space-y-3 rounded-2xl border border-border/80 bg-muted/20 p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Symphonya ERP</p>
          <div className="flex flex-wrap items-center gap-2">
            <SymphonyaOrderBadge symphonya={detail.symphonya} errorHint={detail.symphonyaError} />
            {status.tone === "synced" ? (
              <AdminBadge variant="success">Synced · ERP {detail.symphonya}</AdminBadge>
            ) : null}
            {status.tone === "pending" ? (
              <AdminBadge variant="outline">Not sent yet</AdminBadge>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant={status.tone === "error" ? "destructive" : "outline"}
            size="sm"
            className="h-8 gap-1.5"
            disabled={!canPush || cancelled || sending}
            onClick={onResend}
          >
            <Send className="h-3.5 w-3.5" />
            {sending ? "Sending…" : symphonyaResendButtonLabel(detail.symphonya)}
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5" asChild>
            <Link to="/vadmin/integrari">
              <ExternalLink className="h-3.5 w-3.5" />
              Integrations log
            </Link>
          </Button>
        </div>
      </div>

      {showError ? (
        <AdminAlert variant="error" title="Symphonya push failed">
          <span className="block whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">{errorText}</span>
          {errorHint ? <span className="mt-2 block text-xs leading-relaxed">{errorHint}</span> : null}
        </AdminAlert>
      ) : null}

      {!canPush ? (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Symphonya push applies only to Bucharest Warehouse orders.
        </p>
      ) : null}

      {cancelled ? (
        <p className="text-xs text-muted-foreground">Cancelled orders cannot be sent to Symphonya.</p>
      ) : null}
    </div>
  );
}
